import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';
import type { Permission } from './permissions';

/**
 * The application's record for the signed-in user — not the token's claims.
 * Generated from the API's OpenAPI spec (M0-25), not hand-written: the AC #4
 * proof that the SPA calls at least one endpoint through the generated
 * client. `permissions` is narrowed from the wire type's plain `string[]` —
 * the API has no enum to generate a literal union from, so `Permissions` in
 * `./permissions` stays the one place permission NAMES are declared; this
 * only asserts that what `/api/v1/me` returns is drawn from that set, which
 * is true by construction (`RolePermissions.For` on the API side).
 */
export type CurrentUser = Omit<components['schemas']['CurrentUserResponse'], 'permissions'> & {
  permissions: Permission[];
};

export const currentUserQueryKey = ['me'] as const;

/**
 * Who the signed-in user is, according to the API.
 *
 * Cached for five minutes rather than refetched per screen: it changes only
 * when an administrator changes it, and every guard in the app reads it.
 * The API caches its own subject lookup for the same five minutes (M0-21), so
 * a role change already takes about that long to take effect — matching the
 * two means the UI does not appear to update ahead of what the API will allow.
 *
 * No retry: a 403 here means "not provisioned", which retrying cannot fix.
 */
export function useCurrentUser() {
  return useQuery({
    queryKey: currentUserQueryKey,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/me');
      // `!response.ok || !data`, not `if (error)` alone. openapi-fetch only
      // fills `error` when the failure body parses as the declared
      // problem+json; a gateway 502 is an HTML page, so an `error`-only guard
      // falls through it and returns `undefined`. React Query still fails the
      // query, so the guards still deny — but the reason reaching the console
      // is "query data cannot be undefined" rather than the status code that
      // actually caused it. Observed while browser-testing M1-09 (M0-25).
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/me failed: ${response.status}`);
      }
      return data as CurrentUser;
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}
